// Broad knowledge DOMAINS (kept in sync with backend/domains.py).
// Used for Knowledge Universe "galaxy" clustering + the domain legend.

export const DOMAINS = [
  "Biblical Theology",
  "Divine Council / Heiser Studies",
  "Ancient Near East",
  "Archaeology",
  "Ancient Civilizations",
  "Church History",
  "Apocrypha / Pseudepigrapha",
  "Angels / Demons / Spiritual Beings",
  "Prophecy / Eschatology",
  "UAP / Nonhuman Intelligence",
  "Government Programs",
  "Secret Societies",
  "Consciousness",
  "Dreams / Visions / Altered States",
  "Cryptids / Folklore",
  "High Strangeness",
  "Science / Physics",
  "Technology / AI / Transhumanism",
  "Space / Cosmology",
  "Historical Mysteries",
  "Internet Legends / Modern Folklore",
];

export const DOMAIN_COLORS = {
  "Biblical Theology": "#D8B66A",
  "Divine Council / Heiser Studies": "#BFA7FF",
  "Ancient Near East": "#C07C4A",
  "Archaeology": "#B08D57",
  "Ancient Civilizations": "#D9A066",
  "Church History": "#9E7BB5",
  "Apocrypha / Pseudepigrapha": "#8E7CC3",
  "Angels / Demons / Spiritual Beings": "#C05B7A",
  "Prophecy / Eschatology": "#D98F5B",
  "UAP / Nonhuman Intelligence": "#4FB2A0",
  "Government Programs": "#7E8A9E",
  "Secret Societies": "#8A6D3B",
  "Consciousness": "#7AA7E0",
  "Dreams / Visions / Altered States": "#6FA8DC",
  "Cryptids / Folklore": "#6DAE77",
  "High Strangeness": "#A78BFA",
  "Science / Physics": "#4AA3DF",
  "Technology / AI / Transhumanism": "#4FB286",
  "Space / Cosmology": "#5B7FD1",
  "Historical Mysteries": "#C99A6B",
  "Internet Legends / Modern Folklore": "#E07AAE",
};

const FALLBACK_DOMAIN = "Historical Mysteries";

// Map any specific category -> broad domain (mirror of backend domains.py).
export const CATEGORY_TO_DOMAIN = {
  "Biblical Theology": "Biblical Theology", "Genesis": "Biblical Theology", "Miracles": "Biblical Theology",
  "Comparative Religion": "Biblical Theology", "Religious Law & Sacred Space": "Biblical Theology",
  "Divine Council": "Divine Council / Heiser Studies",
  "Ancient Near East": "Ancient Near East",
  "Archaeology": "Archaeology", "Megaliths": "Archaeology", "Ancient Technology": "Archaeology", "Dead Sea Scrolls": "Archaeology",
  "Ancient Civilizations": "Ancient Civilizations", "Egypt": "Ancient Civilizations", "Lost Civilizations": "Ancient Civilizations",
  "Church History": "Church History",
  "Apocrypha": "Apocrypha / Pseudepigrapha", "Pseudepigrapha": "Apocrypha / Pseudepigrapha",
  "Angelology": "Angels / Demons / Spiritual Beings", "Demonology": "Angels / Demons / Spiritual Beings",
  "Nephilim": "Angels / Demons / Spiritual Beings", "Giants": "Angels / Demons / Spiritual Beings", "Spiritual Warfare": "Angels / Demons / Spiritual Beings",
  "Prophecy": "Prophecy / Eschatology", "Eschatology": "Prophecy / Eschatology",
  "UFO/UAP": "UAP / Nonhuman Intelligence",
  "Government Programs": "Government Programs", "CIA Programs": "Government Programs", "Cybersecurity & Espionage": "Government Programs",
  "Surveillance": "Government Programs", "Cold War": "Government Programs",
  "Secret Societies": "Secret Societies", "Occult History": "Secret Societies",
  "Consciousness": "Consciousness", "Perception & Mind": "Consciousness", "Near Death Experiences": "Consciousness", "Philosophy": "Consciousness",
  "Consciousness Studies": "Consciousness", "Reincarnation & Memory": "Consciousness", "Consciousness & Parapsychology": "Consciousness",
  "Dreams": "Dreams / Visions / Altered States",
  "Cryptids": "Cryptids / Folklore", "Folklore": "Cryptids / Folklore", "Mythology": "Cryptids / Folklore",
  "Anomalous Zones": "High Strangeness", "Numerology & Symbolism": "High Strangeness", "Symbolism": "High Strangeness", "Sacred Geometry": "High Strangeness",
  "Anomalous Phenomena": "High Strangeness", "Fringe Technology": "High Strangeness", "Immortality & Longevity": "High Strangeness",
  "Hidden Places": "High Strangeness", "Ocean Mysteries": "High Strangeness", "Paranormal": "High Strangeness",
  "Science": "Science / Physics", "Mathematics": "Science / Physics", "Quantum Physics": "Science / Physics",
  "Acoustics & Sound": "Science / Physics", "Natural Wonders": "Science / Physics", "Origins of Life": "Science / Physics",
  "Human Origins": "Science / Physics", "Physics & Reality": "Science / Physics",
  "Technology": "Technology / AI / Transhumanism", "Transhumanism": "Technology / AI / Transhumanism", "AI & Robotics": "Technology / AI / Transhumanism",
  "Space & Cosmos": "Space / Cosmology", "Cosmology": "Space / Cosmology", "Lunar Mysteries": "Space / Cosmology",
  "Manuscripts & Codes": "Historical Mysteries", "History & Legends": "Historical Mysteries", "Cataclysm Theories": "Historical Mysteries",
  "Monuments & Messages": "Historical Mysteries", "Unexplained Artifacts": "Historical Mysteries", "Unexplained Deaths": "Historical Mysteries",
  "Urban Legends": "Internet Legends / Modern Folklore", "Internet Culture": "Internet Legends / Modern Folklore",
};

export function domainFor(category) {
  if (!category) return FALLBACK_DOMAIN;
  if (CATEGORY_TO_DOMAIN[category]) return CATEGORY_TO_DOMAIN[category];
  if (DOMAIN_COLORS[category]) return category; // already a broad domain
  return FALLBACK_DOMAIN;
}

export function domainColor(domain) {
  return DOMAIN_COLORS[domain] || DOMAIN_COLORS[FALLBACK_DOMAIN];
}

// Deterministic anchor points ("galaxy" centers) for the domains present in the
// current graph. Arranged on a loose ellipse (wider than tall, `aspect` > 1) so
// clusters spread evenly across a landscape viewport. Returns { domain: {x,y} }.
export function domainAnchors(domainsPresent, radius = 620, aspect = 1.4) {
  const list = DOMAINS.filter((d) => domainsPresent.has(d));
  const extras = [...domainsPresent].filter((d) => !DOMAINS.includes(d));
  const ordered = [...list, ...extras];
  const n = Math.max(1, ordered.length);
  const anchors = {};
  ordered.forEach((d, i) => {
    // golden-angle offset keeps neighbouring domains from lining up in a spoke
    const angle = (i / n) * Math.PI * 2 - Math.PI / 2 + (i % 2) * 0.22;
    anchors[d] = { x: Math.cos(angle) * radius * aspect, y: Math.sin(angle) * radius, domain: d };
  });
  return anchors;
}
